<template>
  <main class="maintained-units">
    <section class="section directory-hero">
      <div class="shell hero-layout">
        <header class="page-head compact">
          <h1>Maintained units</h1>
          <p>
            Equipment currently supported through a Centerfire managed maintenance program.
            Private records are available by scanning the QR code or searching the Centerfire unit number
            to find the machine.
          </p>
        </header>

        <router-link class="explain-button" to="/managed-maintenance-explained">
          <span>What the heck is all this?</span>
          <span class="explain-cta">Demo machines</span>
          <span class="explain-arrow" aria-hidden="true">→</span>
        </router-link>
      </div>
    </section>

    <section class="section directory-section">
      <div class="shell">
        <form class="unit-lookup" novalidate @submit.prevent="findUnit">
          <div class="lookup-copy">
            <h2>Find your machine</h2>
            <p>Enter the letters and numbers printed below the QR code on the Centerfire tag.</p>
          </div>

          <div class="lookup-controls">
            <label class="sr-only" for="unit-number">Machine tag</label>
            <input
              id="unit-number"
              v-model="lookupNumber"
              type="text"
              inputmode="text"
              autocapitalize="characters"
              autocomplete="off"
              maxlength="8"
              placeholder="0103 or ST001"
              aria-describedby="lookup-status"
              @input="limitLookupNumber"
            />
            <button class="btn btn-primary" type="submit">Find machine</button>
          </div>

          <p id="lookup-status" class="lookup-status" :class="{ error: lookupError }" role="status">
            {{ lookupStatus }}
          </p>
        </form>

        <div class="result-heading">
          <div>
            <h2>Equipment under care</h2>
            <p>
              Find your tag below, or type it into the lookup above.
            </p>
          </div>
        </div>

        <MachineTiles
          v-if="tiles.length"
          :tiles="tiles"
          aria-label="Maintained equipment directory"
        />

        <!-- This footnote is a promise, so it has to track what the tiles actually show. It
             said "no photographs" until machine profile pictures were added on 2026-08-15;
             leaving that wording in place would have made the page state something untrue. -->
        <div class="directory-footnote">
          <p>
            Each tile shows the machine's number, its make and model, and a photograph of the
            machine. A machine its owner has asked us to sell also carries a "For sale" marker
            — the listing itself is in Center FireSale. No owner information, location, hours,
            or maintenance history are displayed in this directory. Access to a machine's
            protected record requires its PIN.
          </p>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import MachineTiles from '@/components/MachineTiles.vue';
import { loadMachineTags } from '@/data/maintainedUnits';

// A maintained machine whose owner is selling it carries a marker on its tile. That join used
// to happen right here, between two arrays that were both compiled into the bundle. Listings
// are records now, so the join moved into the service: the tags endpoint returns `forSale` per
// tag, and the listing's internal `ref` never reaches a browser at all.
//
// The marker stays one-way on purpose. The tile points at nothing, and the sale listing never
// links back to the gate, because the record behind the PIN is not the listing's to advertise.
//
// It is AUTOMATIC, not opt-in (Daniel 2026-08-30): someone recognising a machine from its
// photos is a risk inherent to listing it with photos at all - the sale page shows six of
// them - so the chip reveals nothing the listing itself did not. It is a marker, not data.

export default {
  name: 'MaintainedUnitsView',
  components: { MachineTiles },
  data() {
    return {
      realTags: [],
      lookupNumber: '',
      matchedUnit: null,
      hasSearched: false,
      lookupStatus: '',
      lookupError: false,
    };
  },
  computed: {
    // Real machines first, then the examples. Real tags come from the tags endpoint, so the
    // directory reflects what is actually enrolled rather than a list baked in at build time.
    // Real machines only, in natural tag order. The example records live on the explainer
    // page now, so a customer looking for their machine is not scrolling past demos.
    tiles() {
      // Tagged machines first, in natural tag order, then the machines that will never carry a
      // tag as generic type-only tiles. Those link nowhere on purpose: there is no public key
      // to open them with, and their owners reach them through a private per-machine link.
      const tagged = this.realTags.filter((t) => t.lookupNumber)
        .map((t) => ({
          lookupNumber: t.lookupNumber,
          label: t.label,
          forSale: t.forSale === true,
        }))
        .sort((a, b) => a.lookupNumber.localeCompare(b.lookupNumber, undefined, { numeric: true }));
      const generic = this.realTags.filter((t) => !t.lookupNumber && t.publicType)
        .map((t) => ({ publicType: t.publicType }))
        .sort((a, b) => a.publicType.localeCompare(b.publicType));
      return [...tagged, ...generic];
    },
  },
  async created() {
    this.realTags = await loadMachineTags();
  },
  methods: {
    limitLookupNumber() {
      this.lookupNumber = this.lookupNumber.replace(/[^a-z0-9]/gi, '').toUpperCase().slice(0, 8);
      this.matchedUnit = null;
      this.hasSearched = false;
      this.lookupError = false;
      this.lookupStatus = this.lookupNumber.length
        ? 'Enter the complete tag, then select Find machine.'
        : '';
    },
    findUnit() {
      if (!/^[A-Z0-9]{3,8}$/.test(this.lookupNumber)) {
        this.matchedUnit = null;
        this.hasSearched = true;
        this.lookupError = true;
        this.lookupStatus = 'Enter the complete 3–8 character tag.';
        return;
      }

      // Any complete tag goes straight to its protected record. Whether the
      // machine exists is decided behind the PIN, which returns an identical response for
      // an unknown tag and a wrong PIN - so this reveals nothing, and it is the only way
      // a real customer can reach their machine by typing its number.
      //
      // Searching only the demo list here was the bug: a real tag like 0103 came back as
      // "no maintained unit matched", making live machines reachable only by direct URL.
      this.matchedUnit = null;
      this.hasSearched = true;
      this.lookupError = false;
      this.lookupStatus = 'Opening the protected record…';
      this.$router.push(`/maintunits/${this.lookupNumber}`);
    },
  },
};
</script>

<style scoped>
.maintained-units {
  min-height: 100vh;
  background:
    radial-gradient(circle at 82% 9%, rgba(255, 90, 2, 0.13), transparent 27%),
    var(--black);
}

.directory-hero {
  padding-top: clamp(10rem, 15vw, 12rem);
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
}

.hero-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(270px, 0.65fr);
  gap: clamp(2rem, 7vw, 6rem);
  align-items: end;
}

.compact {
  margin: 0;
}

.compact h1 {
  max-width: 12ch;
  font-size: clamp(3rem, 7vw, 6.3rem);
  line-height: 0.94;
  text-transform: uppercase;
}

.compact p {
  margin-top: 1rem;
  font-size: clamp(1rem, 1.5vw, 1.18rem);
}

.explain-button {
  width: min(100%, 290px);
  aspect-ratio: 1;
  justify-self: end;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: clamp(1.3rem, 3vw, 2rem);
  border: 2px solid var(--orange);
  background: rgba(255, 90, 2, 0.06);
  color: var(--white);
  font-family: 'Jost', 'Barlow', sans-serif;
  font-size: clamp(1.8rem, 3.3vw, 2.8rem);
  font-weight: 400;
  line-height: 1.05;
  text-transform: uppercase;
  transition: background-color 0.35s ease, color 0.35s ease, transform 0.35s ease;
}

.explain-button:hover,
.explain-button:focus-visible {
  background: var(--orange);
  color: var(--black);
  transform: translateY(-3px);
}

.explain-cta {
  align-self: stretch;
  padding: 0.7rem 1rem;
  background: var(--orange);
  color: var(--black);
  font-size: clamp(0.95rem, 1.5vw, 1.15rem);
  font-weight: 500;
  letter-spacing: 0.04em;
  text-align: center;
  text-transform: uppercase;
}

/* The whole box is already the link, so the block inverts on hover with it rather than
   behaving like a second, separate control. */
.explain-button:hover .explain-cta,
.explain-button:focus-visible .explain-cta {
  background: var(--black);
  color: var(--orange);
}

.explain-arrow {
  align-self: end;
  font-size: 2rem;
  line-height: 1;
}

.directory-section {
  padding-top: clamp(3rem, 6vw, 5rem);
}

.unit-lookup {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 0.8fr);
  gap: 1rem 2rem;
  align-items: end;
  margin-bottom: clamp(3rem, 6vw, 5rem);
  padding: clamp(1.3rem, 3vw, 2.2rem);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-top: 2px solid var(--orange);
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.025);
}

.lookup-copy h2,
.result-heading h2 {
  margin-bottom: 0.35rem;
  font-size: var(--h2);
  font-weight: 400;
}

.lookup-copy p,
.result-heading > div > p {
  color: rgba(255, 255, 255, 0.76);
}

.lookup-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.65rem;
}

.lookup-controls input {
  width: 100%;
  min-height: 50px;
  padding: 0.7rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.52);
  border-radius: 5px;
  background: var(--black);
  color: var(--white);
  font: inherit;
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 1.25rem;
  letter-spacing: 0.2em;
}

.lookup-controls input:focus {
  border-color: var(--orange);
  outline: 2px solid rgba(255, 90, 2, 0.36);
  outline-offset: 1px;
}

.lookup-controls input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.lookup-status {
  grid-column: 2;
  min-height: 1.4em;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.82rem;
  line-height: 1.4;
}

.lookup-status.error {
  color: var(--orange);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.result-heading {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  align-items: end;
  margin-bottom: 1.5rem;
}

.preview-label {
  color: var(--orange);
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-align: right;
  text-transform: uppercase;
}

.unit-card dl {
  display: grid;
  gap: 1.1rem;
  margin: 0;
}

.unit-card dl > div {
  display: grid;
  gap: 0.25rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
}

.unit-card dl > div:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.unit-card dt {
  color: var(--orange);
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.unit-card dd {
  margin: 0;
  font-family: 'Jost', 'Barlow', sans-serif;
  font-size: clamp(1.2rem, 2vw, 1.55rem);
  font-weight: 400;
}

.directory-footnote {
  max-width: 760px;
  margin-top: 1.4rem;
  padding-left: 1rem;
  border-left: 2px solid rgba(255, 90, 2, 0.7);
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.9rem;
}

@media (max-width: 820px) {
  /* .unit-grid is deliberately NOT listed here. It is the root element of MachineTiles, and
     a child component's root node also carries the parent's scope attribute - so a rule here
     reaches into the component and overrides the breakpoints it defines for itself. The
     component owns its own responsive columns. */
  .hero-layout,
  .unit-lookup {
    grid-template-columns: 1fr;
  }

  .explain-button {
    width: min(100%, 260px);
    justify-self: start;
  }

  .result-heading {
    display: grid;
    align-items: start;
  }

  .lookup-status {
    grid-column: 1;
  }

  .preview-label {
    text-align: left;
  }
}

@media (max-width: 520px) {
  .lookup-controls {
    grid-template-columns: 1fr;
  }
}
</style>
